// src/pages/settings/Templates.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  Textarea,
  Button,
  Spinner,
  useToast,
  Grid,
  FormLabel,
  Flex,
} from '@chakra-ui/react';
import { db } from '../../firebaseConfig';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import useOrgClaims from '../../hooks/useOrgClaims';
import { DEFAULT_TEMPLATES, fill, buildVars, normalizeRecipients } from '../../lib/templates';

const CARD_META = [
  { key: 'pm', title: 'Opportunities (PM & PMDNH)', showSms: true },
  { key: 'cancelled', title: 'Cancelled', showSms: true },
  { key: 'callbacks', title: 'Call Again (REAPP / PTA / BO / NCI)', showSms: true },
  { key: 'survey', title: 'Survey to Surveyors', showSms: false },
];

function ensureShape(obj) {
  const base = JSON.parse(JSON.stringify(DEFAULT_TEMPLATES));
  const out = { ...base, ...(obj || {}) };
  for (const k of Object.keys(base)) {
    out[k] = { ...base[k], ...(obj && obj[k] ? obj[k] : {}) };
  }
  return out;
}

export default function SettingsTemplates() {
  const { orgId, user } = useOrgClaims();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orgProfile, setOrgProfile] = useState({});
  const [templates, setTemplates] = useState(ensureShape(null));

  // Load org profile and templates
  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const orgSnap = await getDoc(doc(db, 'organizations', orgId));
        setOrgProfile(orgSnap.exists() ? orgSnap.data() || {} : {});
        const tSnap = await getDoc(doc(db, 'organizations', orgId, 'settings', 'templates'));
        const t = tSnap.exists() ? ensureShape(tSnap.data()) : ensureShape(null);
        setTemplates(t);
      } catch (e) {
        toast({ title: 'Failed to load', description: e?.message || String(e), status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, toast]);

  function handleChange(scope, field, value) {
    setTemplates((prev) => ({
      ...prev,
      [scope]: { ...prev[scope], [field]: value },
    }));
  }

  async function handleSave() {
    if (!orgId) {
      toast({ title: 'Organisation not set', status: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const toSave = ensureShape(templates);
      await setDoc(
        doc(db, 'organizations', orgId, 'settings', 'templates'),
        {
          ...toSave,
          updatedAt: serverTimestamp(),
          updatedBy: { uid: user?.uid || null, name: user?.displayName || user?.email || 'Admin' },
        },
        { merge: true }
      );
      toast({ title: 'Templates saved', status: 'success' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.message || String(e), status: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function handleRestore(scope) {
    setTemplates((prev) => ({ ...prev, [scope]: { ...DEFAULT_TEMPLATES[scope] } }));
  }

  const sampleLead = useMemo(
    () => ({
      id: 'L-12345',
      customerTitle: 'Mr',
      customerFirstName: 'Alex',
      customerLastName: 'Taylor',
      customerFullName: 'Alex Taylor',
      customerEmail: 'alex@example.com',
      customerPhone: '07123 456789',
      postcode: 'AB12 3CD',
      addressLine1: '1 High Street',
      salespersonName: 'Jamie Sales',
      apptStartAt: new Date(),
      postSaleStage: 'awaiting_survey',
      pmProducts: 'French doors + 2 windows',
      pmPrice: '£7,250',
      pmReason: 'Price too high',
    }),
    []
  );

  function renderPreview(scopeKey) {
    const t = templates[scopeKey] || DEFAULT_TEMPLATES[scopeKey];
    const vars = buildVars({
      lead: sampleLead,
      org: orgProfile,
      report: {},
      extras: { attachments: 'Contract: No, Doors: No' },
    });

    const subject = fill(t.emailSubjectTemplate, vars);
    const body = fill(t.emailBodyTemplate, vars);
    const sms = t.smsTemplate && scopeKey !== 'survey' ? fill(t.smsTemplate, vars) : '';
    const recipients = normalizeRecipients(t.defaultRecipients, sampleLead.customerEmail).join(
      ', '
    );

    return (
      <Box bg="gray.50" borderWidth="1px" borderRadius="md" p={3} fontSize="sm">
        <Text>
          <b>To:</b> {recipients || '(none)'}
        </Text>
        <Text>
          <b>Subject:</b> {subject}
        </Text>
        <Box mt={2}>
          <Text fontWeight="semibold" mb={1}>
            Email body
          </Text>
          <Box whiteSpace="pre-wrap">{body}</Box>
        </Box>
        {scopeKey !== 'survey' && t.smsTemplate && (
          <Box mt={3}>
            <Text fontWeight="semibold" mb={1}>
              SMS
            </Text>
            <Box whiteSpace="pre-wrap">{sms}</Box>
          </Box>
        )}
      </Box>
    );
  }

  if (loading)
    return (
      <HStack spacing={4}>
        <Spinner size="lg" />
        <Text fontSize="lg">Loading…</Text>
      </HStack>
    );

  return (
    <Box borderWidth={1} borderRadius="lg" p={6} mt={0} boxShadow="lg" bg="white">
      <Heading size="lg" mb={6} color="blue.600">
        Templates
      </Heading>
      <Text fontSize="md" color="gray.600" mb={6}>
        Customise the wording for Email and SMS on follow-up pages, and the survey email brief.
        Defaults apply if left blank.
      </Text>

      <VStack align="stretch" spacing={6}>
        {CARD_META.map((meta) => {
          const scope = meta.key;
          const t = templates[scope] || {};
          return (
            <Box key={scope} borderWidth="1px" rounded="md" p={4}>
              <Heading size="sm" mb={3}>
                {meta.title}
              </Heading>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Email subject
                    </FormLabel>
                    <Input
                      size="lg"
                      value={t.emailSubjectTemplate || ''}
                      onChange={(e) => handleChange(scope, 'emailSubjectTemplate', e.target.value)}
                      placeholder="Subject template"
                    />
                  </Box>
                  <Box>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Email body
                    </FormLabel>
                    <Textarea
                      size="lg"
                      rows={8}
                      value={t.emailBodyTemplate || ''}
                      onChange={(e) => handleChange(scope, 'emailBodyTemplate', e.target.value)}
                      placeholder="Body template"
                    />
                  </Box>
                  {meta.showSms && (
                    <Box>
                      <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                        SMS text
                      </FormLabel>
                      <Input
                        size="lg"
                        value={t.smsTemplate || ''}
                        onChange={(e) => handleChange(scope, 'smsTemplate', e.target.value)}
                        placeholder="SMS template"
                      />
                    </Box>
                  )}
                  <Box>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Default recipients (comma-separated)
                    </FormLabel>
                    <Input
                      size="lg"
                      value={t.defaultRecipients || ''}
                      onChange={(e) => handleChange(scope, 'defaultRecipients', e.target.value)}
                      placeholder="e.g. team@org.com,sales@org.com"
                    />
                  </Box>
                  <HStack mt={2}>
                    <Button size="sm" onClick={() => handleRestore(scope)} variant="ghost">
                      Restore defaults
                    </Button>
                  </HStack>
                </VStack>

                <Box>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                    Preview
                  </FormLabel>
                  {renderPreview(scope)}
                </Box>
              </Grid>
            </Box>
          );
        })}
      </VStack>

      <Flex justify="flex-end" pt={4}>
        <Button colorScheme="blue" size="lg" onClick={handleSave} isLoading={saving}>
          Save templates
        </Button>
      </Flex>
    </Box>
  );
}
